import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { useEffect, useState } from "react";
import { FileViewer, PreviewFile } from "./file-viewer";

interface FilePreviewDialogProps {
  files: PreviewFile[];
  selectedIndex: number | null;
  onSelectedIndexChange: (index: number | null) => void;
}

const MIN_ZOOM = 1;
const ZOOM_STEP = 0.1;

export const FilePreviewDialog = ({
  files,
  selectedIndex,
  onSelectedIndexChange,
}: FilePreviewDialogProps) => {
  const [zoom, setZoom] = useState<number>(MIN_ZOOM);

  useEffect(() => {
    setZoom(MIN_ZOOM);
  }, [selectedIndex]);

  const isOpen = selectedIndex !== null;
  const currentFile = selectedIndex !== null ? files[selectedIndex] : undefined;

  const handleZoomIn = () => setZoom((prev) => prev + ZOOM_STEP);
  const handleZoomOut = () =>
    setZoom((prev) => Math.max(MIN_ZOOM, prev - ZOOM_STEP));

  const handlePrevious = () => {
    if (selectedIndex !== null && selectedIndex > 0) {
      onSelectedIndexChange(selectedIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedIndex !== null && selectedIndex < files.length - 1) {
      onSelectedIndexChange(selectedIndex + 1);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => onSelectedIndexChange(null)}>
      <DialogContent className="bg-white w-full max-w-4xl h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-4 py-2 flex flex-row items-center justify-between">
          <DialogTitle className="text-lg font-semibold">
            {currentFile?.name ?? ""}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Visualização do arquivo {currentFile?.name ?? ""}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-between items-center px-4 py-2 border-b">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevious}
            disabled={selectedIndex === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Anterior
          </Button>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="icon" onClick={handleZoomOut}>
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium">
              {Math.round(zoom * 100)}%
            </span>
            <Button variant="outline" size="icon" onClick={handleZoomIn}>
              <ZoomIn className="h-4 w-4" />
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNext}
            disabled={selectedIndex === files.length - 1}
          >
            Próximo
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {currentFile?.url ? (
            <FileViewer
              file={currentFile}
              zoom={zoom}
              className="w-full h-full object-contain"
              fallback={
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">
                    Não é possível visualizar este arquivo.
                  </p>
                </div>
              }
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground">Carregando arquivo...</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
